import React, { useState, useMemo } from 'react';
import { useProgress } from '../contexts/ProgressContext';
import phasesData from '../data/phases.json';
import chaptersData from '../data/chapters.json';
import { Link } from 'react-router-dom';
import UserMenu from '../components/UserMenu';

// Extract a short summary of the first lesson/objectives from each chapter's content.
const extractShortSummary = (chapter) => {
  if (!chapter) return 'No content available.';
  const content = chapter.content || '';
  // Try to grab the first 'Learning Objectives' block first
  const objMatch = content.match(/## Learning Objectives([\s\S]*?)(?=##\s|$)/);
  if (objMatch) {
    const objectives = objMatch[1]
      .split('\n')
      .filter(line => /^\d+\./.test(line.trim()))
      .slice(0, 3)
      .map(line => line.replace(/^\d+\.\s*/, '').trim());
    if (objectives.length > 0) return objectives.join(' • ');
  }
  // Fallback: first non-empty paragraph after title
  const lines = content.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#') && !l.startsWith('|'));
  return lines.slice(0, 2).join(' ').slice(0, 200) + (lines.join(' ').length > 200 ? '…' : '');
};

const RevisionModal = ({ weekNumber, chapters, onClose }) => {
  if (!chapters || chapters.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[85vh] overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 border border-teal/40 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-800/95 backdrop-blur-sm border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-teal/80 font-semibold">Week {weekNumber} — Sunday Revision</p>
            <h3 className="text-xl sm:text-2xl font-bold text-white mt-0.5">Recap of Last {chapters.length} Chapters</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close revision summary"
            className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-80px)] px-6 py-5 space-y-5">
          {chapters.map((c, idx) => (
            <div
              key={c.day}
              className="group relative pl-12 pb-5 border-b border-gray-700/40 last:border-b-0"
            >
              {/* Day badge */}
              <div className="absolute left-0 top-0 w-9 h-9 rounded-lg bg-gradient-to-br from-teal to-green-500 flex items-center justify-center text-white font-bold text-sm shadow-md">
                {c.day}
              </div>
              <div className="flex flex-col gap-1.5">
                <h4 className="text-base sm:text-lg font-semibold text-white leading-tight">
                  {c.title?.replace(/^Chapter \d+:\s*/, '') || `Chapter ${c.day}`}
                </h4>
                <p className="text-sm text-gray-300 leading-relaxed">{extractShortSummary(c)}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal"></span>
                    Phase {c.phase}
                  </span>
                  <Link
                    to={`/chapter/${c.day}`}
                    onClick={onClose}
                    className="ml-auto text-teal hover:text-teal/80 font-medium hover:underline"
                  >
                    Open chapter →
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 bg-gray-800/80 px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-teal text-white font-semibold hover:bg-teal/90 transition-colors shadow-md"
          >
            Got it, back to calendar
          </button>
        </div>
      </div>
    </div>
  );
};

const QuizScore = ({ score }) => {
  const size = 32;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="absolute top-1 right-1 w-8 h-8">
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-gray-700"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="text-teal"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
        {score}
      </span>
    </div>
  );
};

const Dashboard = () => {
  const { 
    calculateCompletionPercentage, 
    calculateAverageScore,
    isDayUnlocked,
    isChapterCompleted,
    progress,
    isQuizAttempted,
    getQuizScore,
    isCertificateEligible
  } = useProgress();

  const completionPercentage = calculateCompletionPercentage();
  const averageScore = calculateAverageScore();
  const certificateEligible = isCertificateEligible();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-navy to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <header className="text-center mb-12">
          <div className="flex justify-end items-center gap-3 mb-4">
            {certificateEligible ? (
              <Link
                to="/certificate"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-teal to-accent text-white font-semibold text-sm hover:shadow-lg transition-all hover:scale-105"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Certificate
              </Link>
            ) : (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700/50 text-gray-500 font-semibold text-sm cursor-not-allowed border border-gray-600/50"
                title="Complete all 30 days with an average score of 60% or more to unlock your certificate"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Certificate Locked
              </div>
            )}
            <UserMenu />
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            30-Day Stock Market Learning Challenge
          </h1>
          <p className="text-xl text-gray-300">
            Master stock market fundamentals through structured learning
          </p>
        </header>

        {/* Overall Progress Card */}
        <div className="bg-gray-800 rounded-xl shadow-2xl p-8 mb-12">
          <h2 className="text-3xl font-semibold text-white mb-6">Your Progress</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Completion Progress */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-300">Overall Completion</span>
                <span className="text-2xl font-bold text-teal">{completionPercentage}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-4">
                <div 
                  className="bg-gradient-to-r from-teal to-accent h-4 rounded-full transition-all duration-500"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
              <p className="text-gray-400 mt-2">
                {progress.completedDays.length} of 30 chapters completed
              </p>
            </div>

            {/* Average Score */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-300">Average Quiz Score</span>
                <span className="text-2xl font-bold text-accent">{averageScore}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-4">
                <div 
                  className="bg-gradient-to-r from-accent to-purple-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${averageScore}%` }}
                />
              </div>
              <p className="text-gray-400 mt-2">
                {progress.quizAttempted.length} of 30 quizzes attempted
              </p>
            </div>
          </div>
        </div>

        {/* Phases Grid */}
        <div className="mb-12">
          <h2 className="text-3xl font-semibold text-white mb-6">Learning Phases</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {phasesData.map((phase) => {
              const completedInPhase = phase.chapters.filter(day => 
                progress.completedDays.includes(day)
              ).length;
              const phaseProgress = ((completedInPhase / phase.chapters.length) * 100).toFixed(0);

              return (
                <Link 
                  key={phase.phaseId}
                  to={`/phase/${phase.phaseId}`}
                  className="bg-gray-800 hover:bg-gray-750 rounded-lg p-6 transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-white">Phase {phase.phaseId}</h3>
                    <span className="text-sm font-medium text-teal">{phaseProgress}%</span>
                  </div>
                  <p className="text-gray-300 mb-4">{phase.title}</p>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-teal h-2 rounded-full transition-all duration-500"
                      style={{ width: `${phaseProgress}%` }}
                    />
                  </div>
                  <p className="text-gray-400 text-sm mt-2">
                    {completedInPhase} of {phase.chapters.length} chapters
                  </p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* 30-Day Challenge - Calendar View (7x5 with Sunday Revision) */}
        <RevisionCalendar
          isDayUnlocked={isDayUnlocked}
          isChapterCompleted={isChapterCompleted}
          isQuizAttempted={isQuizAttempted}
          getQuizScore={getQuizScore}
        />
      </div>
    </div>
  );
};

// Extract just the first paragraph + key terms from a chapter content as a very-short summary
const extractQuickRecap = (chapter) => {
  if (!chapter) return '';
  const content = chapter.content || '';
  // Try Conceptual Overview
  const coMatch = content.match(/## Conceptual Overview([\s\S]*?)(?=##\s|$)/);
  if (coMatch) {
    const para = coMatch[1].trim().split('\n\n')[0].replace(/\n/g, ' ').slice(0, 180);
    return para + (para.length >= 180 ? '…' : '');
  }
  // Fallback: first non-heading paragraph
  const lines = content.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#') && !l.startsWith('|') && !l.startsWith('-'));
  return lines.slice(0, 2).join(' ').slice(0, 160) + (lines.join(' ').length > 160 ? '…' : '');
};

const RevisionCalendar = ({ isDayUnlocked, isChapterCompleted, isQuizAttempted, getQuizScore }) => {
  const [revisionModal, setRevisionModal] = useState(null); // { week, chapters }

  // Build 35-cell layout (5 rows x 7 cols). Sunday cells (col index 6 of each row)
  // contain a revision widget summarizing the 6 prior chapter days.
  // Total chapter days: 30. Row 1 → days 1–6 + Revision. Row 2 → days 7–12 + Revision. Etc.
  // We stop placing once all 30 chapters are placed (Row 5 only needs days 29 & 30).
  const cells = useMemo(() => {
    const layout = [];
    let dayCursor = 1;
    for (let row = 0; row < 5 && dayCursor <= 30; row++) {
      for (let col = 0; col < 6 && dayCursor <= 30; col++) {
        layout.push({ type: 'day', day: dayCursor });
        dayCursor++;
      }
      // 7th column is Sunday - only add revision cell if we placed at least one day this row
      // For row 4 (days 25-30), all 6 slots filled → Revision of 25-30
      // For row 5, only days 29 & 30 placed (dayCursor=31 after) → no revision needed
      const rowDays = [];
      for (let d = dayCursor - 6; d < dayCursor; d++) {
        if (d >= 1 && d <= 30) rowDays.push(d);
      }
      if (rowDays.length === 6) {
        layout.push({ type: 'revision', week: row + 1, days: rowDays });
      } else {
        layout.push({ type: 'empty' });
      }
    }
    return layout;
  }, []);

  const handleRevisionClick = (cell) => {
    if (cell.type !== 'revision') return;
    const chapters = cell.days
      .map(d => chaptersData.find(c => c.day === d))
      .filter(Boolean);
    if (chapters.length > 0) {
      setRevisionModal({ week: cell.week, chapters });
    }
  };

  return (
    <>
      <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <div>
              <h2 className="text-3xl font-semibold text-white">30-Day Challenge Calendar</h2>
              <p className="text-gray-400 text-sm mt-1">5-week plan • 6 study days + Sunday revision each week</p>
            </div>
            <div className="flex items-center gap-4 text-xs font-medium text-gray-400 bg-gray-800/80 px-4 py-2 rounded-lg border border-gray-700">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-teal to-green-500"></span> Completed</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-teal"></span> Unlocked</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span> Revision</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-700"></span> Locked</span>
            </div>
          </div>

          <div className="bg-gray-800/50 border border-gray-700/60 rounded-2xl p-4 sm:p-6 shadow-2xl backdrop-blur-sm">
            {/* Weekday / Day of Week Header */}
            <div className="grid grid-cols-7 gap-2 sm:gap-3 mb-3 text-center">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayName, index) => (
                <div
                  key={dayName}
                  className={`text-xs sm:text-sm font-semibold uppercase tracking-wider py-1.5 rounded-md border ${
                    index === 6
                      ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30'
                      : 'text-teal/80 bg-gray-800/80 border-gray-700/40'
                  }`}
                >
                  <span className="hidden sm:inline">{dayName}</span>
                  <span className="sm:hidden">D{index + 1}</span>
                </div>
              ))}
            </div>

            {/* Calendar Grid (7 Columns × 5 Rows) */}
            <div className="grid grid-cols-7 gap-2 sm:gap-3">
              {cells.map((cell, idx) => {
                if (cell.type === 'empty') {
                  return <div key={`empty-${idx}`} className="hidden sm:block" />;
                }

                if (cell.type === 'revision') {
                  const chapters = cell.days.map(d => chaptersData.find(c => c.day === d)).filter(Boolean);
                  const completedCount = cell.days.filter(d => isChapterCompleted(d)).length;
                  const attemptedCount = cell.days.filter(d => isQuizAttempted(d)).length;
                  const allCompleted = completedCount === 6;

                  return (
                    <button
                      key={`rev-${cell.week}`}
                      type="button"
                      onClick={() => handleRevisionClick(cell)}
                      className={`
                        group relative aspect-[16/10] sm:aspect-[16/10] rounded-lg p-1.5 sm:p-2 flex flex-col justify-between text-left
                        transition-all duration-300 border overflow-hidden cursor-pointer
                        ${allCompleted
                          ? 'bg-gradient-to-br from-yellow-500/25 via-amber-700/20 to-gray-800 border-yellow-500/60 hover:border-yellow-400 hover:shadow-lg hover:shadow-yellow-500/20'
                          : 'bg-gray-800/80 border-yellow-500/30 hover:border-yellow-400 hover:bg-gray-750 hover:shadow-md'}
                      `}
                    >
                      {/* Top Row: Revision Label + Status */}
                      <div className="flex items-center justify-between gap-1 leading-none">
                        <span className={`text-[11px] sm:text-xs font-bold tracking-tight ${allCompleted ? 'text-yellow-300' : 'text-yellow-400'}`}>
                          Rev {cell.week}
                        </span>
                        <svg className="w-3.5 h-3.5 text-yellow-400 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 2a7 7 0 100 14A7 7 0 009 2zm.75 4.75a.75.75 0 00-1.5 0v3.5l-2.22 2.22a.75.75 0 101.06 1.06l2.44-2.44a.75.75 0 00.22-.53V6.75z" />
                        </svg>
                      </div>

                      {/* Middle: Revision Description */}
                      <div className="flex-1 my-0.5 flex flex-col justify-center">
                        <p className="text-[9px] sm:text-[10px] font-bold text-yellow-200/90 uppercase tracking-wider">Sunday</p>
                        <p className="text-[10px] sm:text-[11px] font-medium leading-tight text-yellow-100/80 line-clamp-1 sm:line-clamp-2">
                          Revise {cell.week === 1 ? 'Week 1' : `Week ${cell.week}`}
                        </p>
                        <p className="text-[8px] sm:text-[9px] text-yellow-200/60 mt-0.5 leading-tight hidden sm:block">
                          Click for recap
                        </p>
                      </div>

                      {/* Bottom Indicator Line */}
                      <div className="w-full h-0.5 rounded-full bg-yellow-900/40 overflow-hidden">
                        <div
                          className="h-full bg-yellow-400 transition-all duration-300"
                          style={{ width: `${(completedCount / 6) * 100}%` }}
                        />
                      </div>

                      {/* Mini completion badge */}
                      {completedCount > 0 && (
                        <span className="absolute top-0.5 right-0.5 bg-yellow-500/30 text-yellow-200 text-[8px] sm:text-[9px] font-bold rounded-full px-1 py-px leading-none">
                          {completedCount}/6
                        </span>
                      )}
                    </button>
                  );
                }

                // Regular day cell
                const { day } = cell;
                const unlocked = isDayUnlocked(day);
                const completed = isChapterCompleted(day);
                const quizAttempted = isQuizAttempted(day);
                const score = getQuizScore(day);
                const chapterTitle = chaptersData.find(c => c.day === day)?.title?.replace(/^Chapter \d+:\s*/, '') || `Chapter ${day}`;

                return (
                  <Link
                    key={day}
                    to={unlocked ? `/chapter/${day}` : '#'}
                    onClick={(e) => !unlocked && e.preventDefault()}
                    className={`
                      group relative aspect-[16/10] sm:aspect-[16/10] rounded-lg p-1.5 sm:p-2 flex flex-col justify-between
                      transition-all duration-300 border
                      ${completed 
                        ? 'bg-gradient-to-br from-teal/20 via-green-900/30 to-gray-800 border-teal/50 hover:border-teal hover:shadow-lg hover:shadow-teal/20' 
                        : unlocked 
                          ? 'bg-gray-800/90 border-gray-700 hover:border-teal/60 hover:bg-gray-750 hover:shadow-md' 
                          : 'bg-gray-900/40 border-gray-800/60 text-gray-600 cursor-not-allowed opacity-50'}
                    `}
                  >
                    {/* Top Row: Day Label & Badge */}
                    <div className="flex items-center justify-between gap-1 leading-none">
                      <span className={`text-[11px] sm:text-xs font-bold tracking-tight ${completed ? 'text-teal' : unlocked ? 'text-white' : 'text-gray-600'}`}>
                        Day {day < 10 ? `0${day}` : day}
                      </span>

                      {quizAttempted ? (
                        <div className="scale-65 sm:scale-75 origin-top-right -mr-1 -mt-1">
                          <QuizScore score={score} />
                        </div>
                      ) : completed ? (
                        <span className="p-0.5 rounded-full bg-teal/20 text-teal">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      ) : !unlocked ? (
                        <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      ) : null}
                    </div>

                    {/* Middle: Title Preview */}
                    <p className={`text-[9px] sm:text-[11px] font-medium leading-tight line-clamp-1 sm:line-clamp-2 my-0.5 ${unlocked ? 'text-gray-300 group-hover:text-white' : 'text-gray-600'}`}>
                      {chapterTitle}
                    </p>

                    {/* Bottom Indicator Line */}
                    <div className="w-full h-0.5 rounded-full bg-gray-700/50 overflow-hidden">
                      <div className={`h-full transition-all duration-300 ${completed ? 'bg-teal w-full' : quizAttempted ? 'bg-accent w-1/2' : 'w-0'}`} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Revision Modal */}
        {revisionModal && (
          <RevisionModal
            weekNumber={revisionModal.week}
            chapters={revisionModal.chapters}
            onClose={() => setRevisionModal(null)}
          />
        )}
      </>
  );
};

export default Dashboard;
